import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { doPost, doPut, s3upload } from '../../../redux/api/actions';
import {
    GET_AUDIO_PRESIGNED_URL,
    CREATE_AUDIO_TRACK,
    UPDATE_AUDIO_TRACK
} from '../../../redux/api/constants';
import './AddTrackModal.css';

function AddTrackModal({ tourId, track, onClose, onSuccess }) {
    const dispatch = useDispatch();
    const accessToken = useSelector(state => state.Auth.accessToken);
    const presignedUrl = useSelector(state => state.API.audioPresignedUrl);

    const [audioFile, setAudioFile] = useState(null);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [latitude, setLatitude] = useState('');
    const [longitude, setLongitude] = useState('');
    const [sequenceOrder, setSequenceOrder] = useState(0);
    const [duration, setDuration] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (track) {
            setTitle(track.title || '');
            setDescription(track.description || '');
            setLatitude(track.lat || '');
            setLongitude(track.lng || '');
            setSequenceOrder(track.sequenceOrder || 0);
            setDuration(track.durationSeconds);
        }
    }, [track]);

    // Handle audio file selection and extract duration
    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('audio/')) {
            setErrors({ audioFile: 'Please select an audio file (MP3, M4A, WAV, etc.)' });
            return;
        }

        setAudioFile(file);
        setErrors({ ...errors, audioFile: null });

        // Extract duration using Web Audio API
        try {
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const arrayBuffer = await file.arrayBuffer();
            const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
            const durationSeconds = Math.floor(audioBuffer.duration);
            setDuration(durationSeconds);
            audioContext.close();
        } catch (error) {
            console.error('Error extracting audio duration:', error);
            setErrors({ ...errors, audioFile: 'Could not read audio file duration' });
        }
    };

    // Handle presigned URL response and upload
    useEffect(() => {
        if (presignedUrl && audioFile) {
            // Upload file to S3
            dispatch(s3upload([audioFile], [presignedUrl.presigned_url]));

            // Create track record
            const trackData = {
                title,
                description,
                latitude: parseFloat(latitude),
                longitude: parseFloat(longitude),
                sequence_order: parseInt(sequenceOrder),
                duration_seconds: duration,
                filename: audioFile.name,
            };

            dispatch(doPost(CREATE_AUDIO_TRACK, `tourguide/tours/${tourId}/tracks`, accessToken, trackData));
            setUploading(false);
            onSuccess();
        }
    }, [presignedUrl, audioFile, dispatch, title, description, latitude, longitude, sequenceOrder, duration, tourId, accessToken, onSuccess]);

    const validate = () => {
        const newErrors = {};

        if (!title.trim()) newErrors.title = 'Title is required';
        if (!latitude || isNaN(latitude) || latitude < -90 || latitude > 90) {
            newErrors.latitude = 'Valid latitude required (-90 to 90)';
        }
        if (!longitude || isNaN(longitude) || longitude < -180 || longitude > 180) {
            newErrors.longitude = 'Valid longitude required (-180 to 180)';
        }
        if (!track && !audioFile) newErrors.audioFile = 'Audio file is required';
        if (sequenceOrder < 0) newErrors.sequenceOrder = 'Sequence must be 0 or greater';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!validate()) return;

        setUploading(true);

        if (track) {
            // Update existing track (metadata only, no file upload)
            const updateData = {
                title,
                description,
                latitude: parseFloat(latitude),
                longitude: parseFloat(longitude),
                sequence_order: parseInt(sequenceOrder),
            };

            dispatch(doPut(UPDATE_AUDIO_TRACK, `tourguide/tours/${tourId}/tracks/${track.trackId}`, accessToken, updateData));
            setUploading(false);
            onSuccess();
        } else {
            // Create new track: request presigned URL
            const urlRequest = {
                filename: audioFile.name,
                content_type: audioFile.type,
            };

            dispatch(doPost(GET_AUDIO_PRESIGNED_URL, `tourguide/tours/${tourId}/tracks/presigned-url`, accessToken, urlRequest));
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>{track ? 'Edit Audio Track' : 'Add Audio Track'}</h3>
                    <button className="close-btn" onClick={onClose}>&times;</button>
                </div>

                <form onSubmit={handleSubmit} className="track-form">
                    {!track && (
                        <div className="form-section">
                            <label className="form-label">Audio File *</label>
                            <input
                                type="file"
                                accept="audio/*"
                                onChange={handleFileChange}
                                className={`form-input ${errors.audioFile ? 'error' : ''}`}
                            />
                            {errors.audioFile && <span className="error-text">{errors.audioFile}</span>}
                            {audioFile && duration && (
                                <p className="file-info">
                                    {audioFile.name} ({Math.floor(duration / 60)}:{(duration % 60).toString().padStart(2, '0')})
                                </p>
                            )}
                        </div>
                    )}

                    <div className="form-section">
                        <label className="form-label">Track Title *</label>
                        <input
                            type="text"
                            className={`form-input ${errors.title ? 'error' : ''}`}
                            placeholder="e.g., Welcome to the Tour"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                        />
                        {errors.title && <span className="error-text">{errors.title}</span>}
                    </div>

                    <div className="form-section">
                        <label className="form-label">Description</label>
                        <textarea
                            className="form-input textarea"
                            placeholder="Describe what visitors will hear at this location..."
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows="3"
                        />
                    </div>

                    <div className="form-row">
                        <div className="form-section half">
                            <label className="form-label">Latitude *</label>
                            <input
                                type="number"
                                step="any"
                                className={`form-input ${errors.latitude ? 'error' : ''}`}
                                placeholder="37.7749"
                                value={latitude}
                                onChange={(e) => setLatitude(e.target.value)}
                            />
                            {errors.latitude && <span className="error-text">{errors.latitude}</span>}
                        </div>

                        <div className="form-section half">
                            <label className="form-label">Longitude *</label>
                            <input
                                type="number"
                                step="any"
                                className={`form-input ${errors.longitude ? 'error' : ''}`}
                                placeholder="-122.4194"
                                value={longitude}
                                onChange={(e) => setLongitude(e.target.value)}
                            />
                            {errors.longitude && <span className="error-text">{errors.longitude}</span>}
                        </div>
                    </div>

                    <div className="form-section">
                        <label className="form-label">Sequence Order</label>
                        <input
                            type="number"
                            className={`form-input ${errors.sequenceOrder ? 'error' : ''}`}
                            placeholder="0"
                            value={sequenceOrder}
                            onChange={(e) => setSequenceOrder(e.target.value)}
                            min="0"
                        />
                        {errors.sequenceOrder && <span className="error-text">{errors.sequenceOrder}</span>}
                        <p className="help-text">Order in which this track appears in the tour (0 = first)</p>
                    </div>

                    <div className="modal-actions">
                        <button type="button" className="btn-secondary" onClick={onClose} disabled={uploading}>
                            Cancel
                        </button>
                        <button type="submit" className="btn-primary" disabled={uploading}>
                            {uploading ? 'Uploading...' : track ? 'Update Track' : 'Add Track'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default AddTrackModal;
